<template>
  <!-- 사업구분 선택 화면 (전체 화면, 레이아웃 없음) -->
  <div v-if="!loading && memberData && needsBizGubunSelection" class="biz-gubun-fullscreen">
    <div class="fullscreen-content">
      <div class="select-card">
        <button class="close-fullscreen-btn" @click="closePopup" title="닫기">
          <v-icon size="20">mdi-close</v-icon>
        </button>
        <div class="select-icon">
          <v-icon size="64" color="#1e3a5f">mdi-office-building-cog-outline</v-icon>
        </div>
        <h1 class="facility-name-title">{{ memberData.m_name }}</h1>
        <h2 class="select-title">사업구분을 선택해주세요</h2>
        <p class="select-desc">
          이 시설은 여러 사업을 운영하고 있습니다.<br />
          관리할 사업구분을 선택하면 해당 사업의 데이터만 표시됩니다.
        </p>
        <div class="biz-gubun-options">
          <button
            v-for="biz in bizGubunList"
            :key="biz"
            class="biz-option-btn"
            :style="{ 
              '--biz-color': getBizGubunColor(biz),
              '--biz-color-light': getBizGubunColor(biz) + '15'
            }"
            @click="selectBizGubun(biz)"
          >
            <span class="biz-color-dot" :style="{ backgroundColor: getBizGubunColor(biz) }"></span>
            <span class="biz-name">{{ biz }}</span>
            <v-icon size="18" class="biz-arrow">mdi-chevron-right</v-icon>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 일반 레이아웃 (사업구분 선택 완료 후) -->
  <div v-else class="member-detail-layout">
    <!-- 좌측 고정 사이드바 -->
    <aside class="detail-sidebar" :class="{ collapsed: sidebarCollapsed }">
      <div class="sidebar-header">
        <div class="logo-area" @click="sidebarCollapsed = !sidebarCollapsed">
          <v-icon size="24" color="white">mdi-domain</v-icon>
          <span v-if="!sidebarCollapsed" class="logo-text">CAION</span>
        </div>
        <button class="collapse-btn" @click="sidebarCollapsed = !sidebarCollapsed">
          <v-icon size="18" color="rgba(255,255,255,0.7)">
            {{ sidebarCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}
          </v-icon>
        </button>
      </div>

      <nav class="sidebar-nav">
        <template v-for="item in navMenuItems" :key="item.key">
          <!-- 하위 메뉴 없는 경우 -->
          <template v-if="!item.children">
            <a
              class="nav-item"
              :class="{ active: isActiveMenu(item.key) }"
              @click="navigateToMenu(item.key)"
            >
              <div class="nav-icon">
                <v-icon size="20">{{ item.icon }}</v-icon>
              </div>
              <span v-if="!sidebarCollapsed" class="nav-label">{{ item.title }}</span>
              <div v-if="!sidebarCollapsed && item.badge" class="nav-badge">{{ item.badge }}</div>
            </a>
            <div
              v-if="!sidebarCollapsed && isActiveMenu(item.key) && pageTabs.length"
              class="nav-page-tabs"
            >
              <button
                v-for="tab in pageTabs"
                :key="tab.key || tab.title"
                type="button"
                class="nav-page-tab"
                :class="{ active: pageTabActive === tab.key }"
                :disabled="tab.disabled"
                @click.stop="selectPageTab(tab.key)"
              ><span class="nav-page-tab-mark">-</span>{{ tab.title }}</button>
            </div>
          </template>
          
          <!-- 하위 메뉴 있는 경우 -->
          <div v-else class="nav-group" :class="{ expanded: isMenuExpanded(item.key) }">
            <a
              class="nav-item nav-group-header"
              @click="toggleMenuGroup(item.key)"
            >
              <div class="nav-icon">
                <v-icon size="20">{{ item.icon }}</v-icon>
              </div>
              <span v-if="!sidebarCollapsed" class="nav-label">{{ item.title }}</span>
              <span v-if="!sidebarCollapsed && item.badge" class="nav-admin-badge">{{ item.badge }}</span>
              <v-icon v-if="!sidebarCollapsed" size="16" class="nav-arrow">
                {{ isMenuExpanded(item.key) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </a>
            <div v-if="!sidebarCollapsed && isMenuExpanded(item.key)" class="nav-children">
              <template v-for="child in item.children" :key="child.key">
                <a
                  class="nav-item nav-child"
                  :class="{ active: isActiveMenu(child.key) }"
                  @click="navigateToMenu(child.key)"
                >
                  <div class="nav-icon">
                    <v-icon size="16">{{ child.icon }}</v-icon>
                  </div>
                  <span class="nav-label">{{ child.title }}</span>
                </a>
                <div
                  v-if="isActiveMenu(child.key) && pageTabs.length"
                  class="nav-page-tabs"
                >
                  <button
                    v-for="tab in pageTabs"
                    :key="tab.key || tab.title"
                    type="button"
                    class="nav-page-tab"
                    :class="{ active: pageTabActive === tab.key }"
                    :disabled="tab.disabled"
                    @click.stop="selectPageTab(tab.key)"
                  ><span class="nav-page-tab-mark">-</span>{{ tab.title }}</button>
                </div>
              </template>
            </div>
          </div>
        </template>
      </nav>

      <div class="sidebar-footer">
        <!-- 팝업 모드: 닫기 버튼 -->
        <button v-if="isPopupMode" class="close-btn" @click="closePopup">
          <v-icon size="18">mdi-close</v-icon>
          <span v-if="!sidebarCollapsed">닫기</span>
        </button>
        <!-- 멤버 로그인 모드: 로그아웃 버튼 -->
        <button v-else class="logout-btn" @click="handleLogout">
          <v-icon size="18">mdi-logout</v-icon>
          <span v-if="!sidebarCollapsed">로그아웃</span>
        </button>
      </div>
    </aside>

    <!-- 메인 콘텐츠 영역 -->
    <main class="detail-main" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
      <!-- 상단 헤더 -->
      <header class="detail-header">
        <div class="header-left">
          <div class="breadcrumb">
            <template v-if="isPopupMode">
              <span class="breadcrumb-item">고객관리</span>
              <v-icon size="14" color="#90a4ae">mdi-chevron-right</v-icon>
              <span class="breadcrumb-item current">시설 상세</span>
            </template>
            <template v-else>
              <span class="breadcrumb-item current">내 시설</span>
            </template>
          </div>
          <div class="facility-info">
            <h1 class="facility-name">{{ memberData?.m_name || '시설 정보' }}</h1>
            <!-- 사업구분 선택 영역 -->
            <div class="biz-gubun-selector">
              <template v-if="bizGubunList.length > 1 && !mergeHomeCareBizGubun">
                <!-- 여러 사업구분: 클릭하여 선택 (회계관리·시설 혼합) -->
                <span
                  v-for="biz in bizGubunList"
                  :key="biz"
                  class="biz-chip"
                  :class="{ selected: selectedBizGubun === biz }"
                  :style="{ 
                    backgroundColor: selectedBizGubun === biz ? getBizGubunColor(biz) : 'transparent',
                    borderColor: getBizGubunColor(biz),
                    color: selectedBizGubun === biz ? 'white' : getBizGubunColor(biz)
                  }"
                  @click="selectBizGubun(biz)"
                >
                  {{ biz }}
                </span>
              </template>
              <template v-else-if="mergeHomeCareBizGubun && bizGubunList.length > 1">
                <!-- 방문 전용 다중 사업구분 (임금관리): 모두 선택 상태로 표시 (변경 불가) -->
                <span
                  v-for="biz in bizGubunList"
                  :key="biz"
                  class="biz-chip selected"
                  :style="{ backgroundColor: getBizGubunColor(biz) }"
                >{{ biz }}</span>
              </template>
              <template v-else-if="bizGubunList.length === 1">
                <!-- 단일 사업구분 -->
                <span 
                  class="biz-chip selected"
                  :style="{ backgroundColor: getBizGubunColor(bizGubunList[0]) }"
                >{{ bizGubunList[0] }}</span>
              </template>
              <!-- 상태 뱃지 -->
              <span 
                class="badge status-badge"
                :class="memberData?.m_status === 1 ? 'active' : 'inactive'"
              >{{ memberData?.m_status === 1 ? '활성' : '비활성' }}</span>
            </div>
          </div>
        </div>
        <div class="header-right">
          <div v-if="memberData?.m_longterm_code" class="facility-code">
            <span class="code-label">시설코드</span>
            <span class="code-value">{{ memberData.m_longterm_code }}</span>
          </div>
        </div>
      </header>

      <!-- 콘텐츠 영역 -->
      <div class="detail-content">
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner">
            <v-progress-circular indeterminate color="#1e3a5f" size="48" width="4" />
          </div>
          <p class="loading-text">데이터를 불러오는 중...</p>
        </div>

        <template v-else-if="memberData">
          <router-view />
        </template>

        <div v-else-if="accessDenied" class="error-state">
          <div class="error-icon">
            <v-icon size="64" color="#c62828">mdi-shield-lock-outline</v-icon>
          </div>
          <h2 class="error-title">접근 권한이 없습니다</h2>
          <p class="error-desc">해당 시설의 임금/회계 담당으로 배정되지 않았습니다.</p>
          <button class="error-btn" @click="closePopup">닫기</button>
        </div>

        <div v-else class="error-state">
          <div class="error-icon">
            <v-icon size="64" color="#90a4ae">mdi-file-search-outline</v-icon>
          </div>
          <h2 class="error-title">시설 정보를 찾을 수 없습니다</h2>
          <p class="error-desc">요청하신 시설 정보가 존재하지 않거나 삭제되었습니다.</p>
        </div>
      </div>
    </main>

    <!-- 사업구분 변경 확인 다이얼로그 -->
    <v-dialog v-model="changeBizGubunDialog" max-width="400" persistent @keydown.enter="confirmBizGubunChange">
      <v-card class="confirm-dialog">
        <v-card-title class="confirm-title">
          <span class="confirm-title-text">
            <v-icon class="mr-2" color="#1e3a5f">mdi-swap-horizontal</v-icon>
            사업구분 변경
          </span>
          <button
            type="button"
            class="confirm-close"
            aria-label="닫기"
            @click="cancelBizGubunChange"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="confirm-text">
          <strong>{{ pendingBizGubun }}</strong> 사업구분으로 변경하시겠습니까?<br />
          <span class="confirm-hint">페이지가 새로고침됩니다.</span>
        </v-card-text>
        <v-card-actions class="confirm-actions">
          <v-btn variant="text" class="btn-cancel" @click="cancelBizGubunChange">취소</v-btn>
          <v-spacer />
          <v-btn variant="flat" class="btn-confirm" @click="confirmBizGubunChange">확인</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, provide, watch } from 'vue';
import { BUDGET_LEAVE_KEY, type BudgetLeaveApi } from '@/composables/budgetLeave';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { memberApi } from '@/services/api';
import { createMemberYearMonthState, MEMBER_YEAR_MONTH_KEY } from '@/composables/useMemberYearMonth';
import { createPageTabNav, PAGE_TAB_NAV_KEY } from '@/composables/usePageTabNav';

const route = useRoute();
const router = useRouter();
const store = useStore();

// 현재 사용자 정보 (권한 체크용)
const userOrgCode = computed(() => store.getters.orgCode);
const userRoleLevel = computed(() => store.getters.actualRoleLevel ?? store.getters.roleLevel);
const currentUser = computed(() => store.getters.currentUser);

// 관리자 전용 메뉴용 (ADMIN+ role_level >= 90) — 매니저(60)는 자료요청 등 제외
const isAdmin = computed(() => (userRoleLevel.value || 0) >= 90);
/** 최고관리자(SUPER_ADMIN, role_level 100) */
const isSuperAdmin = computed(() => (userRoleLevel.value || 0) >= 100);

// 팝업 모드 vs 멤버 로그인 모드 판별
// route.meta.isPopup이 있으면 팝업 모드, 없으면 멤버 로그인 모드
const isPopupMode = computed(() => {
  return route.meta.isPopup === true;
});

// 멤버 로그인 모드: JWT idx = m_code (구 세션은 m_idx 숫자일 수 있음 → API에서 호환 조회)
const memberCodeForLogin = computed(() => {
  if (isPopupMode.value) return null;
  const user = currentUser.value;
  if (!user || user.userType !== 'member') return null;
  if (user.idx != null && String(user.idx).trim()) return String(user.idx);
  return null;
});

const resolvedMemberCode = computed(
  () => (route.params.memberCode as string) || memberCodeForLogin.value || ''
);

const loading = ref(true);
const memberData = ref<any>(null);
const accessDenied = ref(false);
const bizGubunColorMap = ref<Record<string, string>>({});

// 사업구분 관련
const selectedBizGubun = ref<string>('');
const changeBizGubunDialog = ref(false);
const pendingBizGubun = ref<string>('');
/** 방문요양·방문목욕 (임금설정 홈/시설 라우트 분기) */
const isHomeCareBiz = computed(
  () => selectedBizGubun.value === '방문요양' || selectedBizGubun.value === '방문목욕'
);

// 회계관리 라우트 — 방문요양/목욕/간호도 사업구분별로 분리 선택
const ACCOUNTING_ROUTE_NAMES = new Set([
  'memberBudgetRevision',
  'memberDetailBudgetRevision',
  'memberBankbook',
  'memberDetailBankbook',
  'memberBankbookTest',
  'memberDetailBankbookTest',
  'memberClosingHistory',
  'memberDetailClosingHistory',
]);

const isAccountingRoute = computed(() => {
  const name = route.name;
  return typeof name === 'string' && ACCOUNTING_ROUTE_NAMES.has(name);
});

// 사업구분 목록 (콤마로 구분된 경우 분리)
const bizGubunList = computed(() => {
  const raw = memberData.value?.m_biz_gubun;
  if (!raw) return [];
  return raw.split(',').map((s: string) => s.trim()).filter(Boolean);
});

// 방문 계열 사업구분만 있는지 확인 (방문요양, 방문목욕, 방문간호)
const isHomeCareBizOnly = computed(() => {
  const list = bizGubunList.value;
  if (list.length === 0) return false;
  return list.every((biz: string) => biz.startsWith('방문'));
});

// 임금관리: 방문 계열 통합(첫 항목) / 회계관리: 사업구분 개별 선택
const mergeHomeCareBizGubun = computed(() => isHomeCareBizOnly.value && !isAccountingRoute.value);

// 다중 사업구분이고 아직 선택되지 않은 경우 (임금·방문 통합 시 제외)
const needsBizGubunSelection = computed(() => {
  if (mergeHomeCareBizGubun.value) return false;
  return bizGubunList.value.length > 1 && !selectedBizGubun.value;
});

// 사업구분 선택 (초기 선택 또는 변경 요청)
const selectBizGubun = (biz: string) => {
  // 이미 선택된 사업구분과 같으면 무시
  if (selectedBizGubun.value === biz) return;
  
  // 임금·방문 통합 모드에서는 변경 불가 (첫 번째 사업구분 유지)
  if (mergeHomeCareBizGubun.value && selectedBizGubun.value) return;
  
  const memberCode = route.params.memberCode as string || memberCodeForLogin.value;
  
  // 아직 선택된 사업구분이 없으면 바로 선택 (초기 선택)
  if (!selectedBizGubun.value) {
    selectedBizGubun.value = biz;
    // sessionStorage에 저장
    if (memberCode) {
      sessionStorage.setItem(`bizGubun_${memberCode}`, biz);
    }
    return;
  }
  
  // 이미 선택된 사업구분이 있으면 변경 확인 다이얼로그 표시
  pendingBizGubun.value = biz;
  changeBizGubunDialog.value = true;
};

// 사업구분 변경 취소
const cancelBizGubunChange = () => {
  pendingBizGubun.value = '';
  changeBizGubunDialog.value = false;
};

// 사업구분 변경 확인 → 적절한 페이지로 이동
const confirmBizGubunChange = async () => {
  changeBizGubunDialog.value = false;
  if (!(await confirmBudgetLeave())) {
    cancelBizGubunChange();
    return;
  }
  const memberCode = route.params.memberCode as string || memberCodeForLogin.value;
  const newBizGubun = pendingBizGubun.value;
  
  // sessionStorage에 선택한 사업구분 저장
  if (memberCode) {
    sessionStorage.setItem(`bizGubun_${memberCode}`, newBizGubun);
  }
  
  budgetLeave.value?.allowNextUnload();
  window.location.reload();
};

// 사이드바 상태
const sidebarCollapsed = ref(false);

// 네비게이션 메뉴 아이템
interface NavMenuItem {
  key: string;
  title: string;
  icon: string;
  routeName?: string;
  badge?: string | number;
  adminOnly?: boolean;
  /** SUPER_ADMIN(role_level ≥ 100) 전용 */
  superAdminOnly?: boolean;
  /** 최소 role_level (예: 관리자 60+) */
  minRole?: number;
  /** 임금/회계 담당 메뉴 분리 */
  assigneeType?: 'WAGE' | 'ACCOUNT' | 'COMMON';
  children?: NavMenuItem[];
}

const baseNavMenuItems: NavMenuItem[] = [
  { key: 'facility-info', title: '시설정보', icon: 'mdi-view-dashboard-outline', routeName: 'memberDetailInfo', assigneeType: 'COMMON' },
  { 
    key: 'request-group', 
    title: '자료요청', 
    icon: 'mdi-cloud-download-outline',
    adminOnly: true,
    badge: '관리자전용',
    assigneeType: 'COMMON',
    children: [
      { key: 'request-ltcs', title: '롱텀케어', icon: 'mdi-file-document-check-outline', routeName: 'memberDetailRequestLtcs', assigneeType: 'COMMON' },
      { key: 'request-ces', title: '희망이음', icon: 'mdi-file-document-edit-outline', routeName: 'memberDetailRequestCes', assigneeType: 'COMMON' }
    ]
  },
  {
    key: 'wage-group',
    title: '임금관리',
    icon: 'mdi-cash-multiple',
    assigneeType: 'WAGE',
    children: [
      { key: 'wage-hr', title: '인사관리', icon: 'mdi-account-group', routeName: 'memberDetailWageHr', assigneeType: 'WAGE' },
      { key: 'wage-monthly-setting', title: '임금설정', icon: 'mdi-calendar-month', routeName: 'memberDetailWageMonthlySettingFacility', assigneeType: 'WAGE' },
    ]
  },
  { 
    key: 'accounting-group', 
    title: '회계관리', 
    icon: 'mdi-book-open-variant',
    assigneeType: 'ACCOUNT',
    children: [
      { key: 'budget-revision', title: '예산추경관리', icon: 'mdi-file-document-plus-outline', routeName: 'memberDetailBudgetRevision', assigneeType: 'ACCOUNT' },
      { key: 'bankbook', title: '월별통장내역', icon: 'mdi-calendar-month', routeName: 'memberDetailBankbook', assigneeType: 'ACCOUNT' },
      { key: 'closing-history', title: '마감요청내역', icon: 'mdi-clipboard-check-outline', routeName: 'memberDetailClosingHistory', assigneeType: 'ACCOUNT' },
      { key: 'accounting-reports', title: '회계보고서류', icon: 'mdi-file-chart-outline', routeName: 'memberDetailAccountingReports', assigneeType: 'ACCOUNT' }
    ]
  }
];

/** API access: 관리자90+ / 멤버본인 / 임금·회계 배정 */
const memberAccess = computed(() => {
  const a = memberData.value?.access;
  if (!a) {
    return { isAdmin: false, hasWage: false, hasAccount: false, hasAny: false, isMemberSelf: false };
  }
  return {
    isAdmin: !!a.isAdmin,
    hasWage: !!a.hasWage,
    hasAccount: !!a.hasAccount,
    hasAny: !!a.hasAny,
    isMemberSelf: !!a.isMemberSelf,
  };
});

const canShowNavItem = (item: NavMenuItem): boolean => {
  if (item.adminOnly && !isAdmin.value) return false;
  if (item.superAdminOnly && !isSuperAdmin.value) return false;
  if (item.minRole != null && (userRoleLevel.value || 0) < item.minRole) return false;

  // 관리자(90+): 자료요청만 adminOnly로 제한, 임금·회계는 항상 표시
  if (isAdmin.value) return true;

  const acc = memberAccess.value;
  if (!memberData.value?.access) return true;
  if (acc.isAdmin || acc.isMemberSelf) return true;
  if (item.assigneeType === 'WAGE') return acc.hasWage;
  if (item.assigneeType === 'ACCOUNT') return acc.hasAccount;
  return acc.hasAny;
};

// 사용자 권한에 따라 필터링된 메뉴 아이템
const navMenuItems = computed(() => {
  return baseNavMenuItems
    .filter((item) => canShowNavItem(item))
    .map((item) => {
      if (!item.children?.length) return item;
      return {
        ...item,
        children: item.children.filter((child) => {
          if (!canShowNavItem(child)) return false;
          return true;
        }),
      };
    });
});

// 라우트 이름 변환 (팝업 모드 vs 멤버 로그인 모드)
const getRouteName = (baseRouteName: string) => {
  if (isPopupMode.value) {
    return baseRouteName; // memberDetailInfo, etc.
  }
  // 멤버 로그인 모드: memberDetail 제거
  return baseRouteName.replace('memberDetail', 'member'); // memberInfo, etc.
};

// 펼쳐진 메뉴 그룹
const expandedMenus = ref<string[]>([]);

/** 임금설정 — 방문요양(Home) / 시설(Facility) 라우트 모두 동일 메뉴 */
const isWageMonthlySettingRoute = (routeName: string) => {
  const home = getRouteName('memberDetailWageMonthlySettingHome');
  const facility = getRouteName('memberDetailWageMonthlySettingFacility');
  return routeName === home || routeName === facility;
};

const isNavChildActive = (child: NavMenuItem, currentRouteName: string) => {
  if (child.key === 'wage-monthly-setting') {
    return isWageMonthlySettingRoute(currentRouteName);
  }
  if (!child.routeName) return false;
  return currentRouteName === getRouteName(child.routeName);
};

// 현재 경로에 해당하는 부모 메뉴 그룹 자동 펼치기
const initExpandedMenus = () => {
  const currentRouteName = String(route.name);
  for (const menu of navMenuItems.value) {
    if (menu.children) {
      const hasActiveChild = menu.children.some((child) =>
        isNavChildActive(child, currentRouteName)
      );
      if (hasActiveChild && !expandedMenus.value.includes(menu.key)) {
        expandedMenus.value.push(menu.key);
      }
    }
  }
};

// 현재 활성 메뉴 확인
const isActiveMenu = (menuKey: string) => {
  const currentRouteName = String(route.name);

  if (menuKey === 'wage-monthly-setting') {
    return isWageMonthlySettingRoute(currentRouteName);
  }

  // 1단계 메뉴 확인
  const item = navMenuItems.value.find(m => m.key === menuKey);
  if (item?.routeName) {
    // 팝업 모드: 정확히 일치, 멤버 로그인 모드: memberDetail 제거하고 비교
    const expectedRouteName = getRouteName(item.routeName);
    return currentRouteName === expectedRouteName;
  }
  // 2단계 메뉴 확인
  for (const menu of navMenuItems.value) {
    if (menu.children) {
      const child = menu.children.find(c => c.key === menuKey);
      if (child) {
        return isNavChildActive(child, currentRouteName);
      }
    }
  }
  return false;
};

// 메뉴 그룹 펼침/접기
const toggleMenuGroup = (menuKey: string) => {
  // collapsed 상태에서 그룹 메뉴 클릭 시 사이드바 펼치기
  if (sidebarCollapsed.value) {
    sidebarCollapsed.value = false;
    // 해당 메뉴 그룹도 펼치기
    if (!expandedMenus.value.includes(menuKey)) {
      expandedMenus.value.push(menuKey);
    }
    return;
  }
  
  const idx = expandedMenus.value.indexOf(menuKey);
  if (idx >= 0) {
    expandedMenus.value.splice(idx, 1);
  } else {
    expandedMenus.value.push(menuKey);
  }
};

// 메뉴 그룹 펼침 여부
const isMenuExpanded = (menuKey: string) => {
  return expandedMenus.value.includes(menuKey);
};

// 메뉴 이동
const navigateToMenu = (menuKey: string) => {
  const memberCode = route.params.memberCode as string;

  // 월별임금설정 메뉴 (방문요양/목욕 → 홈 임금설정, 그 외 → 시설)
  if (menuKey === 'wage-monthly-setting') {
    const wageRouteName = isHomeCareBiz.value
      ? getRouteName('memberDetailWageMonthlySettingHome')
      : getRouteName('memberDetailWageMonthlySettingFacility');
    if (isPopupMode.value) {
      router.push({ name: wageRouteName, params: { memberCode } });
    } else {
      router.push({ name: wageRouteName });
    }
    return;
  }

  // 1단계 메뉴 확인
  const item = navMenuItems.value.find(m => m.key === menuKey);
  if (item?.routeName) {
    const routeName = getRouteName(item.routeName);
    if (isPopupMode.value) {
      router.push({ name: routeName, params: { memberCode } });
    } else {
      router.push({ name: routeName });
    }
    return;
  }
  
  // 2단계 메뉴 확인
  for (const menu of navMenuItems.value) {
    if (menu.children) {
      const child = menu.children.find(c => c.key === menuKey);
      if (child?.routeName) {
        const routeName = getRouteName(child.routeName);
        if (isPopupMode.value) {
          router.push({ name: routeName, params: { memberCode } });
        } else {
          router.push({ name: routeName });
        }
        return;
      }
    }
  }
};

// 사업구분 색상
const getBizGubunColor = (bizGubun: string) => {
  return bizGubunColorMap.value[bizGubun] || '#607d8b';
};

// 멤버 데이터 로드
const loadMemberData = async (memberCode: string) => {
  loading.value = true;
  memberData.value = null;
  accessDenied.value = false;
  
  try {
    const params: Record<string, any> = {};
    if (store.getters.isImpersonating) {
      params.roleLevel = store.getters.roleLevel;
      params.userIdx = currentUser.value?.idx;
    }
    const memberRes = await memberApi.getById(memberCode, params);
    memberData.value = memberRes.data?.data || null;
    
    const bizRes = await memberApi.getBizGubunList();
    if (bizRes.data?.data) {
      const colorMap: Record<string, string> = {};
      bizRes.data.data.forEach((item: any) => {
        colorMap[item.bg_name] = item.bg_color;
      });
      bizGubunColorMap.value = colorMap;
    }
    
    // 사업구분 자동 선택 로직
    if (memberData.value?.m_biz_gubun) {
      const list = memberData.value.m_biz_gubun.split(',').map((s: string) => s.trim()).filter(Boolean);
      const allHomeCare = list.every((biz: string) => biz.startsWith('방문'));
      const routeName = route.name;
      const onAccounting =
        typeof routeName === 'string' && ACCOUNTING_ROUTE_NAMES.has(routeName);

      if (list.length === 1) {
        selectedBizGubun.value = list[0];
      } else if (allHomeCare && !onAccounting) {
        // 임금 등: 방문 전용 다중 → 첫 번째만 (청구 API는 별도 통합)
        selectedBizGubun.value = list[0];
      } else if (list.length > 1) {
        // 회계관리·시설 혼합: sessionStorage에서 이전 선택값 복원
        const savedBizGubun = sessionStorage.getItem(`bizGubun_${memberCode}`);
        if (savedBizGubun && list.includes(savedBizGubun)) {
          selectedBizGubun.value = savedBizGubun;
        } else {
          selectedBizGubun.value = list[0];
        }
      }
    }
    
    if (memberData.value?.m_name) {
      document.title = `${memberData.value.m_name} - ${route.meta.title || '시설 상세정보'}`;
    }
  } catch (error: any) {
    console.error('멤버 정보 조회 오류:', error);
    if (error.response?.status === 403) {
      accessDenied.value = true;
    }
  } finally {
    loading.value = false;
  }
};

const budgetLeave = ref<BudgetLeaveApi | null>(null);
provide(BUDGET_LEAVE_KEY, budgetLeave);

async function confirmBudgetLeave(): Promise<boolean> {
  if (!budgetLeave.value) return true;
  return budgetLeave.value.confirmDiscard();
}

// 팝업 닫기
const closePopup = async () => {
  if (!(await confirmBudgetLeave())) return;
  budgetLeave.value?.allowNextUnload();
  window.close();
};

// 로그아웃 (멤버 로그인 모드)
const handleLogout = async () => {
  if (!(await confirmBudgetLeave())) return;
  budgetLeave.value?.allowNextUnload();
  store.dispatch('logout');
  window.location.href = '/login';
};

// 시설별 연월 (sessionStorage 연동 — m_code 기준)
const activeMemberCode = computed(() => {
  if (memberData.value?.m_code) return String(memberData.value.m_code);
  return resolvedMemberCode.value;
});

const memberYearMonth = createMemberYearMonthState(activeMemberCode);

// 하위 컴포넌트에 데이터 제공
provide('memberData', memberData);
provide('bizGubunColorMap', bizGubunColorMap);
provide('selectedBizGubun', selectedBizGubun);
provide('loading', loading);
provide(MEMBER_YEAR_MONTH_KEY, memberYearMonth);

const pageTabNav = createPageTabNav();
provide(PAGE_TAB_NAV_KEY, pageTabNav);
const pageTabs = computed(() => pageTabNav.tabs.value);
const pageTabActive = computed(() => pageTabNav.activeKey.value);
const selectPageTab = (key: string) => pageTabNav.select(key);
provide('memberSelectedYear', memberYearMonth.selectedYear);
provide('memberSelectedMonth', memberYearMonth.selectedMonth);

// 라우트·로그인 멤버코드 변경 시 재조회
watch(
  resolvedMemberCode,
  (code) => {
    if (code) {
      loadMemberData(code);
    } else if (!isPopupMode.value && !currentUser.value) {
      loading.value = false;
    } else if (!code) {
      loading.value = false;
      memberData.value = null;
    }
  },
  { immediate: true }
);

watch(currentUser, (user) => {
  if (!isPopupMode.value && user?.userType === 'member' && resolvedMemberCode.value) {
    loadMemberData(resolvedMemberCode.value);
  }
});

/** 담당 권한 없는 메뉴로 직접 진입 시 시설정보로 이동 */
const enforceAssigneeRouteAccess = () => {
  if (!memberData.value?.access || accessDenied.value) return;
  const acc = memberAccess.value;
  // 관리자 JWT(90+)·API access·멤버 본인 — 임금/회계 진입 허용
  if (isAdmin.value || acc.isAdmin || acc.isMemberSelf) return;

  const name = String(route.name || '');
  const isAccounting = ACCOUNTING_ROUTE_NAMES.has(name);
  const isWage =
    name.includes('Wage') ||
    name.includes('wage') ||
    name.includes('Beneficiary') ||
    name.includes('beneficiary');

  if (isAccounting && !acc.hasAccount) {
    router.replace({ name: getRouteName('memberDetailInfo'), params: route.params });
    return;
  }
  if (isWage && !acc.hasWage) {
    router.replace({ name: getRouteName('memberDetailInfo'), params: route.params });
  }
};

// 라우트 변경 시 메뉴 그룹 펼치기
onMounted(() => {
  initExpandedMenus();
});

watch(() => route.name, () => {
  initExpandedMenus();
  enforceAssigneeRouteAccess();
});

watch(memberAccess, () => {
  enforceAssigneeRouteAccess();
});
</script>
